<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const store = useStore();

const showCreateDialog = ref(false);
const selectedCampaign = ref(null);
const showDetailDialog = ref(false);
const campaignStats = ref(null);

const uiFlags = computed(() => store.getters['emailCampaigns/getUIFlags']);
const isFetching = computed(() => uiFlags.value.isFetching);
const emailCampaigns = computed(
  () => store.getters['emailCampaigns/getEmailCampaigns']
);
const hasNoCampaigns = computed(
  () => emailCampaigns.value.length === 0 && !isFetching.value
);

const inboxes = computed(() => {
  const allInboxes = store.getters['inboxes/getInboxes'];
  return allInboxes.filter(i => i.channel_type === 'Channel::Email');
});

const formData = ref({
  title: '',
  subject: '',
  body: '',
  inbox_id: '',
});

onMounted(() => {
  store.dispatch('emailCampaigns/get');
  store.dispatch('inboxes/get');
});

const statusLabel = status => {
  const labels = {
    draft: 'Draft',
    scheduled: 'Scheduled',
    sending: 'Sending...',
    sent: 'Sent',
    paused: 'Paused',
    cancelled: 'Cancelled',
  };
  return labels[status] || status;
};

const statusColor = status => {
  const colors = {
    draft: 'bg-slate-100 text-slate-700',
    scheduled: 'bg-blue-100 text-blue-700',
    sending: 'bg-yellow-100 text-yellow-700',
    sent: 'bg-green-100 text-green-700',
    paused: 'bg-orange-100 text-orange-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  return colors[status] || 'bg-slate-100 text-slate-700';
};

const resetForm = () => {
  formData.value = { title: '', subject: '', body: '', inbox_id: '' };
  showCreateDialog.value = false;
};

const handleCreate = async () => {
  try {
    await store.dispatch('emailCampaigns/create', {
      email_campaign: {
        ...formData.value,
        campaign_type: 'one_off',
        audience: [],
      },
    });
    useAlert(t('CAMPAIGN.EMAIL.CREATE_SUCCESS'));
    resetForm();
  } catch (error) {
    useAlert(t('CAMPAIGN.EMAIL.CREATE_ERROR'));
  }
};

const handleSendNow = async campaign => {
  try {
    await store.dispatch('emailCampaigns/sendNow', campaign.id);
    useAlert(t('CAMPAIGN.EMAIL.SEND_SUCCESS'));
    store.dispatch('emailCampaigns/get');
  } catch (error) {
    useAlert(t('CAMPAIGN.EMAIL.SEND_ERROR'));
  }
};

const handleDelete = async campaign => {
  if (!window.confirm(t('CAMPAIGN.EMAIL.CONFIRM_DELETE'))) return;
  try {
    await store.dispatch('emailCampaigns/delete', campaign.id);
    useAlert(t('CAMPAIGN.EMAIL.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CAMPAIGN.EMAIL.DELETE_ERROR'));
  }
};

const handleViewStats = async campaign => {
  selectedCampaign.value = campaign;
  showDetailDialog.value = true;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${campaign.account_id}/email_campaigns/${campaign.id}/stats`
    );
    campaignStats.value = data;
  } catch {
    campaignStats.value = null;
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.EMAIL.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.EMAIL.NEW_CAMPAIGN')"
    @click="showCreateDialog = true"
    @close="showCreateDialog = false"
  >
    <!-- Create dialog -->
    <template #action>
      <div
        v-if="showCreateDialog"
        class="absolute right-0 z-20 w-96 p-4 mt-2 bg-white border border-slate-100 rounded-xl shadow-lg dark:bg-slate-800 dark:border-slate-700"
      >
        <h3 class="mb-3 text-sm font-semibold text-slate-800 dark:text-slate-100">
          {{ t('CAMPAIGN.EMAIL.CREATE_TITLE') }}
        </h3>
        <div class="flex flex-col gap-3">
          <div>
            <label class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300">
              {{ t('CAMPAIGN.EMAIL.FORM.INBOX') }}
            </label>
            <select
              v-model="formData.inbox_id"
              class="w-full px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
            >
              <option value="" disabled>
                {{ t('CAMPAIGN.EMAIL.FORM.SELECT_INBOX') }}
              </option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300">
              {{ t('CAMPAIGN.EMAIL.FORM.TITLE') }}
            </label>
            <input
              v-model="formData.title"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
              :placeholder="t('CAMPAIGN.EMAIL.FORM.TITLE_PLACEHOLDER')"
            />
          </div>
          <div>
            <label class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300">
              {{ t('CAMPAIGN.EMAIL.FORM.SUBJECT') }}
            </label>
            <input
              v-model="formData.subject"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
              :placeholder="t('CAMPAIGN.EMAIL.FORM.SUBJECT_PLACEHOLDER')"
            />
          </div>
          <div>
            <label class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300">
              {{ t('CAMPAIGN.EMAIL.FORM.BODY') }}
            </label>
            <textarea
              v-model="formData.body"
              rows="4"
              class="w-full px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
              :placeholder="t('CAMPAIGN.EMAIL.FORM.BODY_PLACEHOLDER')"
            />
          </div>
          <div class="flex justify-end gap-2">
            <button
              class="px-3 py-1.5 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
              @click="resetForm"
            >
              {{ t('CAMPAIGN.EMAIL.FORM.CANCEL') }}
            </button>
            <button
              class="px-3 py-1.5 text-sm text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50"
              :disabled="!formData.title || !formData.subject || !formData.body || !formData.inbox_id"
              @click="handleCreate"
            >
              {{ t('CAMPAIGN.EMAIL.FORM.CREATE') }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Loading -->
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="hasNoCampaigns"
      class="flex flex-col items-center justify-center gap-2 pt-14 text-slate-500"
    >
      <span class="text-3xl">📧</span>
      <p class="text-base font-medium">
        {{ t('CAMPAIGN.EMAIL.EMPTY_STATE.TITLE') }}
      </p>
      <p class="text-sm text-slate-400">
        {{ t('CAMPAIGN.EMAIL.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>

    <!-- Campaign list -->
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="campaign in emailCampaigns"
        :key="campaign.id"
        class="flex items-center justify-between p-4 bg-white border rounded-xl border-slate-100 dark:bg-slate-800 dark:border-slate-700"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
              {{ campaign.title }}
            </h3>
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-full"
              :class="statusColor(campaign.status)"
            >
              {{ statusLabel(campaign.status) }}
            </span>
          </div>
          <p class="mt-1 text-xs text-slate-500 truncate">
            {{ t('CAMPAIGN.EMAIL.CARD.SUBJECT') }}: {{ campaign.subject }}
          </p>
          <p
            v-if="campaign.sent_count > 0"
            class="mt-1 text-xs text-slate-400"
          >
            {{ t('CAMPAIGN.EMAIL.CARD.SENT') }}: {{ campaign.sent_count }}
            · {{ t('CAMPAIGN.EMAIL.CARD.OPENED') }}: {{ campaign.opened_count || 0 }}
            · {{ t('CAMPAIGN.EMAIL.CARD.CLICKED') }}: {{ campaign.clicked_count || 0 }}
          </p>
        </div>
        <div class="flex items-center gap-2 ml-4">
          <button
            v-if="campaign.status === 'draft' || campaign.status === 'scheduled'"
            class="px-3 py-1.5 text-xs font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600"
            @click="handleSendNow(campaign)"
          >
            {{ t('CAMPAIGN.EMAIL.ACTIONS.SEND') }}
          </button>
          <button
            class="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
            @click="handleViewStats(campaign)"
          >
            {{ t('CAMPAIGN.EMAIL.ACTIONS.STATS') }}
          </button>
          <button
            v-if="campaign.status !== 'sending'"
            class="px-2 py-1.5 text-xs text-red-500 hover:text-red-700"
            @click="handleDelete(campaign)"
          >
            ✕
          </button>
        </div>
      </div>
    </div>

    <!-- Stats dialog -->
    <div
      v-if="showDetailDialog && selectedCampaign"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      @click.self="showDetailDialog = false"
    >
      <div class="w-full max-w-md p-6 bg-white rounded-2xl shadow-xl dark:bg-slate-800">
        <h3 class="mb-4 text-base font-semibold text-slate-800 dark:text-slate-100">
          {{ selectedCampaign.title }} — {{ t('CAMPAIGN.EMAIL.STATS_TITLE') }}
        </h3>
        <div v-if="campaignStats" class="grid grid-cols-2 gap-3">
          <div
            v-for="(value, key) in campaignStats"
            :key="key"
            class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700"
          >
            <p class="text-xs text-slate-500">{{ key.replace(/_/g, ' ') }}</p>
            <p class="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {{ typeof value === 'number' && key.includes('rate') ? value + '%' : value }}
            </p>
          </div>
        </div>
        <div v-else class="py-4 text-center text-slate-400">
          Loading...
        </div>
        <div class="flex justify-end mt-4">
          <button
            class="px-4 py-2 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
            @click="showDetailDialog = false"
          >
            {{ t('CAMPAIGN.EMAIL.FORM.CANCEL') }}
          </button>
        </div>
      </div>
    </div>
  </CampaignLayout>
</template>
